// Entra External ID — Native Authentication (email OTP) côté WEB.
// L'OTP est saisi DANS l'app (pas de redirection vers la page hébergée d'Entra,
// donc pas d'invite "accéder à d'autres applications").
// Flux : initiate -> challenge (envoie l'OTP) -> submit (oob=code) -> tokens.
// Repli sign-up si l'utilisateur n'existe pas encore.
//
// IMPORTANT : les endpoints natifs de ciamlogin.com n'émettent pas de CORS et
// déclencheraient l'invite WAM si on les appelait depuis le navigateur. On passe
// donc par le PROXY backend cc-attack (/auth/native/*), qui relaie côté serveur.
import { SCOPES, decodeJwt, getEntraConfig } from './entraConfig';
import type { Session } from './entraAuth.web';
import type { AuthMode, SignupAttributes } from './nativeAuth.types';

const CT_KEY = 'ccjaw.native.ct';      // continuation_token + mode en cours
const SESSION_KEY = 'ccjaw.session';   // même clé que le flux redirection
const CHALLENGE = 'oob redirect';


// path est de la forme '/oauth2/v2.0/initiate' -> relayé via le backend :
// ${apiBase}/auth/native/oauth2/v2.0/initiate
async function postForm(path: string, fields: Record<string, string>): Promise<any> {
  const base = getEntraConfig().apiBaseUrl.replace(/\/$/, '');
  const res = await fetch(`${base}/auth/native${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(fields).toString(),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// Étape 1 : démarre le flux. NE déclenche l'OTP QUE pour un compte EXISTANT.
// Pour un email inconnu -> retourne {mode:'signup'} SANS envoyer d'OTP, pour que
// l'app collecte d'abord prénom/nom/âge/pays puis appelle startSignup().
export async function startEmailOtp(email: string): Promise<{ mode: AuthMode; target?: string }> {
  const cfg = getEntraConfig();
  const cid = cfg.clientId;

  const r = await postForm('/oauth2/v2.0/initiate', {
    client_id: cid, username: email, challenge_type: CHALLENGE,
  });
  if (!r.ok) {
    if (r.data?.error === 'user_not_found' || r.status === 400) {
      return { mode: 'signup' }; // l'app affiche le formulaire d'inscription
    }
    throw new Error(`initiate: ${r.data?.error} ${r.data?.error_description || ''}`);
  }

  // Compte existant -> challenge (envoie l'OTP)
  const c = await postForm('/oauth2/v2.0/challenge', {
    client_id: cid, continuation_token: r.data.continuation_token, challenge_type: CHALLENGE,
  });
  if (!c.ok) throw new Error(`challenge: ${c.data?.error} ${c.data?.error_description || ''}`);
  if (c.data.challenge_type && c.data.challenge_type !== 'oob') {
    throw new Error('Native auth indisponible (challenge non-OTP) — vérifier la config du tenant.');
  }
  sessionStorage.setItem(CT_KEY, JSON.stringify({ ct: c.data.continuation_token, mode: 'signin' }));
  return { mode: 'signin', target: c.data.challenge_target_label };
}

// Inscription : crée le compte Entra avec les attributs BUILT-IN dupliqués
// (prénom/nom/pays) puis envoie l'OTP. L'âge (custom) n'est PAS envoyé à Entra :
// il est géré en base (cf. AuthContext.submitOtp -> POST /me/profile).
export async function startSignup(email: string, attrs: SignupAttributes): Promise<{ target?: string }> {
  const cfg = getEntraConfig();
  const cid = cfg.clientId;
  const attributes = JSON.stringify({
    givenName: attrs.givenName,
    surname: attrs.surname,
    country: attrs.country,
  });

  const r = await postForm('/signup/v1.0/start', {
    client_id: cid, username: email, challenge_type: CHALLENGE, attributes,
  });
  if (!r.ok) throw new Error(`signup/start: ${r.data?.error} ${r.data?.error_description || ''}`);

  const c = await postForm('/signup/v1.0/challenge', {
    client_id: cid, continuation_token: r.data.continuation_token, challenge_type: CHALLENGE,
  });
  if (!c.ok) throw new Error(`signup/challenge: ${c.data?.error} ${c.data?.error_description || ''}`);
  if (c.data.challenge_type && c.data.challenge_type !== 'oob') {
    throw new Error('Native auth indisponible (challenge non-OTP) — vérifier la config du tenant.');
  }
  sessionStorage.setItem(CT_KEY, JSON.stringify({ ct: c.data.continuation_token, mode: 'signup' }));
  return { target: c.data.challenge_target_label };
}

// Étape 3 : soumet le code OTP saisi dans l'app -> tokens.
export async function submitOtp(code: string): Promise<Session> {
  const cfg = getEntraConfig();
  const cid = cfg.clientId;
  const saved = JSON.parse(sessionStorage.getItem(CT_KEY) || '{}');
  if (!saved.ct) throw new Error('Aucun flux OTP en cours.');
  let ct: string = saved.ct;

  if (saved.mode === 'signup') {
    // sign-up continue (oob) -> nouveau continuation_token, puis token
    const cont = await postForm('/signup/v1.0/continue', {
      client_id: cid, continuation_token: ct, grant_type: 'oob', oob: code,
    });
    if (!cont.ok) throw new Error(`signup/continue: ${cont.data?.error} ${cont.data?.error_description || ''}`);
    ct = cont.data.continuation_token;
    const tok = await postForm('/oauth2/v2.0/token', {
      client_id: cid, continuation_token: ct, grant_type: 'continuation_token', scope: SCOPES.join(' '),
    });
    if (!tok.ok) throw new Error(`token: ${tok.data?.error} ${tok.data?.error_description || ''}`);
    return persist(tok.data);
  }

  // sign-in : token avec grant_type=oob
  const tok = await postForm('/oauth2/v2.0/token', {
    client_id: cid, continuation_token: ct, grant_type: 'oob', oob: code, scope: SCOPES.join(' '),
  });
  if (!tok.ok) throw new Error(`token: ${tok.data?.error} ${tok.data?.error_description || ''}`);
  return persist(tok.data);
}

function persist(data: any): Session {
  const session: Session = {
    accessToken: data.access_token,
    expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
    claims: decodeJwt(data.access_token),
    refreshToken: data.refresh_token,
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.removeItem(CT_KEY);
  return session;
}

// Déconnexion NATIVE : purge locale uniquement. La native auth ne crée pas de
// session navigateur côté CIAM -> aucune redirection vers ciamlogin (donc pas
// d'écran "Pick an account" ni d'invite). On revient à l'accueil, déconnecté.
export function logout(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(CT_KEY);
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') window.location.assign('/');
}
